/**
 * Maps form data from the lead modal to the suggestions API format.
 */

const EVENT_TYPE_MAP: Record<string, string> = {
  corporate_retreat: "Overnight retreat",
  away_day: "Away Day",
  corporate_party: "Corporate Party",
  team_building: "Team Building",
  conference: "Conference",
  product_launch: "Product Launch",
  other: "Other",
};

/**
 * Parse participants range string to get lower bound number.
 * e.g. "51-100" → 51, "500+" → 500, "75" → 75
 */
export function parseParticipants(participantsStr: string): number {
  if (!participantsStr) return 50;
  const match = participantsStr.match(/(\d+)/);
  return match ? parseInt(match[1], 10) : 50;
}

/**
 * Parse budget string like "15 000 €" or "15000 €+" to numeric value.
 */
export function parseBudgetNumeric(budgetStr: string): number {
  if (!budgetStr) return 15000;
  const cleaned = budgetStr.replace(/[^\d]/g, "");
  const num = parseInt(cleaned, 10);
  return isNaN(num) ? 15000 : num;
}

/**
 * Convert numeric budget to display range string.
 */
export function getBudgetRange(budget: number): string {
  if (budget <= 10000) return "$10K or less";
  if (budget <= 25000) return "$10K-25K";
  if (budget <= 50000) return "$25K-50K";
  if (budget <= 100000) return "$50K-100K";
  if (budget <= 200000) return "$100K-200K";
  return "$200K+";
}

/**
 * Build start_date and end_date from form data.
 * If exact dates: use ISO strings.
 * If month only: default to 15th-17th of that month.
 */
function getMonthNumber(monthName: string): number {
  const months: Record<string, number> = {
    janvier: 0, février: 1, mars: 2, avril: 3, mai: 4, juin: 5,
    juillet: 6, août: 7, septembre: 8, octobre: 9, novembre: 10, décembre: 11,
    // English fallback
    january: 0, february: 1, march: 2, april: 3, may: 4, june: 5,
    july: 6, august: 7, september: 8, october: 9, november: 10, december: 11,
  };
  return months[monthName.toLowerCase()] ?? -1;
}

function getEnglishMonthName(monthIndex: number): string {
  const names = ["January", "February", "March", "April", "May", "June",
    "July", "August", "September", "October", "November", "December"];
  return names[monthIndex] || "January";
}

interface DateInfo {
  start_date: string | null;
  end_date: string | null;
  month: string | null;
}

export function buildDates(startDate: string, endDate: string, monthStr?: string): DateInfo {
  // If exact dates are provided (ISO or dd/MM/yyyy format)
  if (startDate && !monthStr) {
    return {
      start_date: startDate,
      end_date: endDate || startDate,
      month: null,
    };
  }

  // If month is provided
  if (monthStr) {
    const monthIndex = getMonthNumber(monthStr);
    if (monthIndex === -1) {
      return { start_date: null, end_date: null, month: monthStr };
    }

    const now = new Date();
    let year = now.getFullYear();
    // If month is in the past this year, use next year
    if (monthIndex <= now.getMonth()) {
      year += 1;
    }

    const start = new Date(year, monthIndex, 15);
    const end = new Date(year, monthIndex, 17);

    return {
      start_date: start.toISOString(),
      end_date: end.toISOString(),
      month: getEnglishMonthName(monthIndex),
    };
  }

  return { start_date: null, end_date: null, month: null };
}

export interface SuggestionsPayload {
  event_type: string;
  participants: number;
  participants_range: string;
  budget: number;
  budget_range: string;
  start_date: string | null;
  end_date: string | null;
  month: string | null;
  destination: string;
  market: string;
  locale?: string;
}

export function buildSuggestionsPayload(eventDetails: {
  eventType: string;
  participantsCount: string;
  budget: string;
  startDate: string;
  endDate: string;
  month?: string;
  location: string;
}): SuggestionsPayload {
  const eventType = eventDetails.eventType.startsWith("autre:")
    ? "Other"
    : (EVENT_TYPE_MAP[eventDetails.eventType] || "Other");

  const participants = parseParticipants(eventDetails.participantsCount);
  const budgetNum = parseBudgetNumeric(eventDetails.budget);
  const dates = buildDates(eventDetails.startDate, eventDetails.endDate, eventDetails.month);

  return {
    event_type: eventType,
    participants,
    participants_range: eventDetails.participantsCount || `${participants}`,
    budget: budgetNum,
    budget_range: getBudgetRange(budgetNum),
    start_date: dates.start_date,
    end_date: dates.end_date,
    month: dates.month,
    destination: eventDetails.location || "France",
    market: "France",
    locale: "fr",
  };
}
